import React, {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";

import Loading from "@/components/Loading";
import Page from "@/components/Page";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import VLFCard from "@/components/Card";
import ViewField from "@/components/Input/ViewField";
import VLFModal from "@/components/Modal";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";

import {PlayerRoleAPI} from "@/api/player_role";
import {ModuleAPI} from "@/api/module";

import {
	AREA_LABELS,
	ERROR_OPERATION,
	SUCCESSFUL_OPERATION,
} from "@/utils/constants";

function SettingsPlayerRolePage(props) {
	const {handleNotification} = props;
	const {id} = useParams();
	const navigate = useNavigate();

	const [loading, setLoading] = useState(false);
	const [openHandleConfirm, setOpenHandleConfirm] = useState(false);
	const [roleData, setRoleData] = useState({});

	const [allModules, setAllModules] = useState([]);
	const [assignedModules, setAssignedModules] = useState([]);
	const [modulesLoading, setModulesLoading] = useState(false);

	const getRole = async () => {
		setLoading(true);
		try {
			const response = await PlayerRoleAPI.getById(id);
			setRoleData(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const loadModules = async () => {
		setModulesLoading(true);
		try {
			const allMods = await ModuleAPI.list();
			setAllModules(allMods || []);

			// Per ogni modulo, carico i ruoli per sapere a quali è già assegnato
			const assigned = [];
			await Promise.all(
				(allMods || []).map(async (mod) => {
					try {
						const roles = await ModuleAPI.getRoles(mod.id);
						const found = (roles || []).find((r) => r.playerRoleId === id);
						if (found) {
							assigned.push(mod);
						}
					} catch (_) {
						// ignora errori su singoli moduli
					}
				}),
			);
			setAssignedModules(assigned);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setModulesLoading(false);
		}
	};

	useEffect(() => {
		getRole();
		loadModules();
	}, []);

	const deleteRole = async () => {
		setLoading(true);
		try {
			await PlayerRoleAPI.delete(id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			navigate("/settings/player-roles");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		} finally {
			setOpenHandleConfirm(false);
		}
	};

	const addModuleAssignment = async (moduleId) => {
		setModulesLoading(true);
		try {
			await ModuleAPI.addRole(moduleId, id);
			await loadModules();
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			setModulesLoading(false);
		}
	};

	const removeModuleAssignment = async (moduleId) => {
		setModulesLoading(true);
		try {
			await ModuleAPI.removeRole(moduleId, id);
			await loadModules();
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			setModulesLoading(false);
		}
	};

	const mainConfiguration = [
		{value: roleData?.code, label: "Codice", size: "col-sm-12 col-lg-4"},
		{value: roleData?.department, label: "Reparto", size: "col-sm-12 col-lg-4"},
		{
			value: roleData?.active === false ? "Inattivo" : "Attivo",
			label: "Disponibilità nelle scelte",
			size: "col-sm-12 col-lg-4",
		},
		{value: roleData?.name, label: "Nome", size: "col-sm-12 col-lg-4"},
		{
			value: AREA_LABELS[roleData?.area] || roleData?.area || "",
			label: "Ruolo principale",
			size: "col-sm-12 col-lg-4",
		},
		{
			value: roleData?.sorting,
			label: "Ordinamento",
			size: "col-sm-12 col-lg-4",
		},
	];

	const mainActions = [
		{
			icon: <ArrowBackIcon />,
			title: "Indietro",
			onClick: () => navigate("/settings/player-roles"),
			color: "info",
		},
		{
			icon: <EditIcon />,
			title: "Modifica",
			onClick: () =>
				navigate("/settings/player-roles/" + roleData?.id + "/edit"),
		},
		{
			icon: <DeleteIcon />,
			title: "Elimina",
			onClick: () => setOpenHandleConfirm(true),
			color: "error",
		},
	];

	const breadcrumbsElements = [
		{label: "Ruoli giocatori", path: "/settings/player-roles"},
		{label: "/"},
	];

	const availableModules = allModules.filter(
		(m) => !assignedModules.find((a) => a.id === m.id),
	);

	return (
		<Page className="page-container">
			<Loading visible={loading} />
			<Breadcrumbs
				elements={breadcrumbsElements}
				current={roleData ? roleData.name || "" : ""}
				actions={<MainActions actions={mainActions} />}
			/>
			<div className="row g-2 no-margin">
				<div className="col-sm-12 padding-small">
					<VLFCard
						title="Informazioni principali"
						content={
							<div className="row">
								<div className="col-sm-12">
									<ViewField configuration={mainConfiguration} />
								</div>
							</div>
						}
						openable={false}
					/>
				</div>
				<div className="col-sm-12 padding-small">
					<VLFCard
						title="Moduli tattici"
						content={
							<div>
								{/* MODULI SELEZIONATI */}
								<div className="col-sm-12 padding-small">
									<div
										className="list-title"
										style={{fontWeight: 600, marginBottom: 8}}
									>
										Moduli assegnati
									</div>
									{modulesLoading ? (
										<Loading />
									) : assignedModules.length === 0 ? (
										<div className="text-muted" style={{fontSize: 13}}>
											Nessun modulo assegnato
										</div>
									) : (
										<div className="d-flex flex-wrap gap-2">
											{assignedModules.map((mod) => (
												<div
													key={mod.id}
													className="d-flex align-items-center justify-content-between px-2 py-1 rounded"
													style={{
														background: "var(--primary-alpha-10, #e8f0fe)",
														border: "1px solid var(--primary-color, #1a73e8)",
														fontSize: 13,
													}}
												>
													<span>{mod.name}</span>
													<CloseIcon
														fontSize="small"
														style={{cursor: "pointer", marginLeft: 4}}
														onClick={() => removeModuleAssignment(mod.id)}
													/>
												</div>
											))}
										</div>
									)}
								</div>

								{/* MODULI DISPONIBILI */}
								<div
									className="col-sm-12 padding-small"
									style={{marginTop: 16}}
								>
									<div
										className="list-title"
										style={{fontWeight: 600, marginBottom: 8}}
									>
										Seleziona moduli
									</div>
									{availableModules.length === 0 ? (
										<div className="text-muted" style={{fontSize: 13}}>
											Tutti i moduli sono stati assegnati
										</div>
									) : (
										<div className="d-flex flex-wrap gap-2">
											{availableModules.map((mod) => (
												<div
													key={mod.id}
													className="d-flex align-items-center justify-content-between px-2 py-1 rounded"
													style={{
														background: "var(--background-secondary, #f5f5f5)",
														border: "1px solid var(--border-color, #ddd)",
														fontSize: 13,
														cursor: modulesLoading ? "not-allowed" : "pointer",
														opacity: modulesLoading ? 0.5 : 1,
													}}
													onClick={() =>
														!modulesLoading && addModuleAssignment(mod.id)
													}
												>
													<span>{mod.name}</span>
													<AddIcon fontSize="small" style={{marginLeft: 4}} />
												</div>
											))}
										</div>
									)}
								</div>
							</div>
						}
						openable={false}
					/>
				</div>
			</div>
			<VLFModal
				open={openHandleConfirm}
				title="Eliminazione"
				content={
					<div className="mt-2">
						Sei sicuro di voler eliminare questo elemento?
					</div>
				}
				confirm={() => deleteRole()}
				close={() => setOpenHandleConfirm(false)}
				confirmLabel="Conferma"
			/>
		</Page>
	);
}

export default SettingsPlayerRolePage;
